import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isString } from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  switchMap,
} from 'rxjs';

import {
  ColumnId,
  ColumnLinkInfo,
  ComparisonOperator,
  LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { DatasetsApiService, GetPaginatedRequestModel } from '@blaze-shared/datasets-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  DatasetColumn,
  DatasetLinkValue,
  DatasetRow,
  DatasetSchema,
  DEFAULT_ID_COL,
  findColumn,
  getDisplayStringFromLinkValue,
  isDatasetLinkValue,
} from '@blaze-shared/schema-domain';
import { LoadingIndicatorComponent, TooltipDirective } from '@blaze-shared/shared-ui';

import { BlazeTableService } from '../shared/blaze-table.service';
import { BlazeTableRecord } from '../shared/blaze-table.store';
import { TablePopupMenuComponent } from '../table-popup-menu/table-popup-menu.component';

@UntilDestroy()
@Component({
  selector: 'blaze-find-record-panel',
  templateUrl: './find-record-panel.component.html',
  styleUrls: ['./find-record-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TooltipDirective,
    LoadingIndicatorComponent,
    TablePopupMenuComponent,
    CommonModule,
    ReactiveFormsModule,
  ],
})
export class FindRecordPanelComponent implements OnChanges {
  private readonly blazeTableService = inject(BlazeTableService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly schemaService = inject(SchemaService);

  closePanel = output<void>();
  recordSelected = output<BlazeTableRecord>();

  @Input() columns?: DatasetColumn[] = [];
  @Input() link!: ColumnLinkInfo | undefined | null;
  @Input() menuOpened$!: Observable<boolean>;
  @Input() selectedRecord!: BlazeTableRecord | null;
  @Input() selectedRecords!: BlazeTableRecord[] | null;

  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;

  private readonly searchText$$ = new BehaviorSubject<string>('');
  private readonly linkConfig$$ = new BehaviorSubject<ColumnLinkInfo | undefined | null>(null);
  private readonly selectedRecords$ = new BehaviorSubject<BlazeTableRecord[]>([]);

  private schemaIdColMap: Record<string, string> = {};
  private usedRelatedColIds?: string[];
  private usedRelatedColNames?: string[];

  private requestBody?: GetPaginatedRequestModel;

  readonly recordsLoaded = signal(0);
  readonly totalRecords = signal(0);

  anyRecordsLoaded = signal<boolean>(false);
  filteredRecords$!: Observable<BlazeTableRecord[]>;
  nextPageToken?: string | null;
  records = signal<BlazeTableRecord[]>([]);
  rows: DatasetRow[] = [];

  searchFormCtrl = new FormControl<string>('');

  constructor() {
    // Subscribe to search text changes to trigger data fetching
    this.searchFormCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.searchText$$.next(searchValue ?? '');
      });

    combineLatest([this.getRelatedColumns(), this.searchText$$.asObservable().pipe(startWith(''))])
      .pipe(
        map(([{ allRelatedColumns, relatedStringColumns }, searchText]) => {
          this.records.set([]);
          const requestBody: GetPaginatedRequestModel = {
            pagination: {
              maxCount: 100,
            },
            projectColumns: [...new Set(allRelatedColumns || [])],
            filter: !searchText
              ? null
              : ({
                  nodeType: QueryNodeType.Logical,
                  logicalType: LogicalType.Or,
                  children: relatedStringColumns.map((column) => ({
                    nodeType: QueryNodeType.Comparison,
                    column,
                    operator: ComparisonOperator.ContainsText,
                    value: searchText,
                    children: [],
                    displayInfos: [],
                    ignoreIfEmptyValue: false,
                    isFormulaValue: false,
                    isOptional: false,
                  })),
                } as LogicalQueryNode),
          };

          this.requestBody = requestBody;
          this.nextPageToken = undefined;

          return requestBody;
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        const tableId = this.link?.tableId;
        if (!tableId || !this.requestBody) {
          return;
        }
        this.loadNextPage();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedRecords']) {
      this.selectedRecords$.next(this.selectedRecords || []);
    }
    if (changes['link']) {
      this.linkConfig$$.next(this.link);
    }
  }

  scrollBottom($event: any): void {
    if (
      this.nextPageToken &&
      $event.target.scrollHeight - $event.target.scrollTop === $event.target.clientHeight
    ) {
      this.loadNextPage();
    }
  }

  assignRelatedColumns(schema: DatasetSchema | undefined): void {
    this.schemaIdColMap = Object.fromEntries(schema?.columns.map((c) => [c.id, c.name]) || []);
    const linkedColId = this.columns?.find(
      (col) => col.columnType === 'Link' && col.link?.tableId === this.link?.tableId,
    )?.id;
    this.usedRelatedColIds =
      linkedColId &&
      this.columns
        ?.filter((col) => col.columnType === 'Formula' && col.formula?.includes(linkedColId))
        .map((c) => Object.keys(this.schemaIdColMap).find((k) => c.formula?.includes(k)))
        .filter((s): s is string => !!s);
    this.usedRelatedColNames = this.usedRelatedColIds?.map((id) => this.schemaIdColMap[id]);
    const additionalColumns = Array.from(
      new Set(
        [
          this.link?.columnName,
          this.link?.displayColumn,
          schema?.displayColumn,
          schema?.columns.find((col) => col.isId)?.name,
        ]
          .filter(isString)
          .filter(Boolean),
      ),
    );
    this.usedRelatedColNames?.push(...additionalColumns);
  }

  getRecordDisplayValue(record: BlazeTableRecord): string {
    return (
      (isDatasetLinkValue(record?.value) &&
        ((isDatasetLinkValue(record.value?.display) &&
          (getDisplayStringFromLinkValue(record.value.display?.display) ||
            record.value.display?.rowId)) ||
          getDisplayStringFromLinkValue(record.value?.display) ||
          record.value?.rowId)) ||
      (record.value as string) ||
      (typeof record === 'string' ? record : '')
    );
  }

  getRecordLinkedValue(
    record?: BlazeTableRecord['value'] | BlazeTableRecord['value'][] | string,
  ): string {
    if (!Array.isArray(record)) {
      return (
        getDisplayStringFromLinkValue((record as DatasetLinkValue)?.display) ||
        (isDatasetLinkValue(record) ? (record as DatasetLinkValue)?.rowId : undefined) ||
        (record as string)
      );
    }

    return record
      .map((r) => this.getRecordDisplayValue({ value: r } as BlazeTableRecord))
      .join(', ');
  }

  isRecordSelected(record: BlazeTableRecord): boolean {
    return (
      this.selectedRecords?.some(
        (rec) =>
          (rec.value as DatasetLinkValue)?.rowId === (record?.value as DatasetLinkValue)?.rowId,
      ) ||
      (record?.value as DatasetLinkValue)?.rowId ===
        (this.selectedRecord?.value as DatasetLinkValue)?.rowId
    );
  }

  /**
   * If record contains rowId, we should not show it. Instead, we should only show the displayed value.
   * @param record
   * @returns
   */
  isRecordValueVisible(record: BlazeTableRecord): boolean {
    return !record?.tableHasExpandableLinks;
  }

  selectRecord(record: BlazeTableRecord): void {
    this.selectedRecord = record;
    this.recordSelected.emit(record);
  }

  setFilteredRecords(filteredRecords$: Observable<BlazeTableRecord[]>): void {
    this.filteredRecords$ = combineLatest([filteredRecords$, this.selectedRecords$]).pipe(
      map(([filteredRecords, selectedRecords]) => {
        return filteredRecords
          ?.filter(
            (record) =>
              !selectedRecords?.find(
                (selectedRecord) =>
                  (selectedRecord?.value as DatasetLinkValue)?.rowId ===
                  (record.value as DatasetLinkValue)?.rowId,
              ),
          )
          .slice(0, 100);
      }),
    );
  }

  preventEventAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  loadNextPage(): void {
    if (!this.link?.tableId || !this.requestBody) {
      return;
    }

    const nextPage = this.nextPageToken;

    const nextPageRequestBody = {
      ...this.requestBody,
      pagination: {
        ...this.requestBody.pagination,
        ...(nextPage && { pageToken: nextPage }),
      },
    };
    const tableId = this.link.tableId;

    this.datasetsApiService
      .postGetPaginated(tableId, nextPageRequestBody, false)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.nextPageToken = response.pagination.nextPageToken;
        this.records.set(this.records().concat(this.mapRowsToRecords(response.data)));

        this.anyRecordsLoaded.set(true);
        setTimeout(() => {
          const input = this.filterInput.nativeElement;
          const inputLength = input.value.length;

          input.focus();
          input.select();
          input.setSelectionRange(inputLength, inputLength);
        }, 300);
      });
  }

  private getRelatedColumns(): Observable<{
    allRelatedColumns: string[];
    relatedStringColumns: string[];
  }> {
    return this.linkConfig$$.asObservable().pipe(
      filter(Boolean),
      switchMap((link) => this.mapSchemaToRelatedColumns(link.tableId)),
    );
  }

  private mapSchemaToRelatedColumns(tableId: string): Observable<{
    allRelatedColumns: string[];
    relatedStringColumns: string[];
  }> {
    return this.schemaService.obtainSchema<DatasetSchema>(tableId).pipe(
      map((schema) => {
        this.schemaIdColMap = Object.fromEntries(schema?.columns.map((c) => [c.id, c.name]) || []);
        const linkedColId = this.columns?.find(
          (col) => col.columnType === 'Link' && col.link?.tableId === this.link?.tableId,
        )?.id;
        this.usedRelatedColIds =
          (linkedColId &&
            (this.columns
              ?.filter((col) => col.columnType === 'Formula' && col.formula?.includes(linkedColId))
              .map((c) => Object.keys(this.schemaIdColMap).find((k) => c.formula?.includes(k)))
              .filter(Boolean) as string[])) ||
          [];
        this.usedRelatedColNames = this.usedRelatedColIds?.map((id) => this.schemaIdColMap[id]);
        const relatedStringColumns = this.usedRelatedColIds
          ?.map((id) => {
            const column = findColumn(schema?.columns, { id: id as ColumnId });
            if (column?.dataType === 'String' && !column.isId) {
              return this.schemaIdColMap[id];
            }
            return null;
          })
          .filter((s): s is string => Boolean(s));
        const displayColumnSchema = schema?.columns.find(
          (col) => col.name === (this.link?.displayColumn ?? schema?.displayColumn),
        );
        if (displayColumnSchema?.dataType === 'String') {
          relatedStringColumns?.push(displayColumnSchema.name);
        }
        const additionalColumns = Array.from(
          new Set(
            [
              this.link?.columnName,
              this.link?.displayColumn,
              schema?.displayColumn,
              displayColumnSchema?.name,
            ]
              .filter((name) => isString(name))
              .filter(Boolean),
          ),
        );
        this.usedRelatedColNames?.push(...additionalColumns);

        return {
          allRelatedColumns: this.usedRelatedColNames || [],
          relatedStringColumns: relatedStringColumns || [],
        };
      }),
    );
  }

  private mapRowsToRecords(rows: DatasetRow[]): BlazeTableRecord[] {
    if (!this.link) {
      return [];
    }
    const columnName = this.link.columnName || DEFAULT_ID_COL;
    const tableId = this.link.tableId;
    const tableHasExpandableLinks = this.blazeTableService.hasExpandableLinks(tableId);
    return rows.map((row) => {
      const value = row[columnName];
      const displayColumn =
        this.schemaService.getDisplayColumn(tableId, this.rows) ?? this.link?.displayColumn;

      const displayValue = row[displayColumn] || '';
      if (value && !Array.isArray(value) && this.schemaIdColMap) {
        const otherLinkedValues: BlazeTableRecord['otherLinkedValues'] =
          this.usedRelatedColIds?.map((id) => [
            this.schemaIdColMap[id],
            row[this.schemaIdColMap[id]],
          ]);

        const linkValue: DatasetLinkValue = {
          display: displayValue,
          rowId: value,
        };
        return {
          displayColumn,
          displayValue,
          value: linkValue,
          tableHasExpandableLinks,
          otherLinkedValues,
        };
      }
      return {
        displayColumn,
        displayValue,
        value: value,
        tableHasExpandableLinks,
        otherLinkedValues: [],
      };
    });
  }
}
